import type { ChangedFacetDetails } from 'src/components/organisms/ListingsControlsOptions/types';
import type { UIFacet } from 'src/components/types/UIFacet';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { applyFacetOptionChangeToFacets } from 'src/redux/productsQuery/helpers/facetOptionHelpers';
import { adaptStoreToPageURL } from './adaptStoreToPageURL';
import { buildListingsURL } from './buildListingsURL';
import {
  facetKeyToListingsQueryParamName,
  getListingsFacetsAsURLSearchParams,
} from './listings-api/getListingsFacetsAsURLSearchParams';

type StoreFacet = ReduxApplicationState['productsQuery']['facets'][number];
type StoreFacetOption = StoreFacet['options'][number];

type IndexedFacetEntry = {
  facet: StoreFacet;
  optionsByValue: Map<string, StoreFacetOption>;
};

type ResolvedFacetOption = {
  facet: StoreFacet;
  option: StoreFacetOption;
};

const buildFacetsIndex = (
  facets: ReduxApplicationState['productsQuery']['facets'],
): Map<string, IndexedFacetEntry> => {
  const facetsIndex = new Map<string, IndexedFacetEntry>();
  facets.forEach((facet) => {
    facetsIndex.set(facet.key, {
      facet,
      optionsByValue: new Map(facet.options.map((option) => [option.value, option])),
    });
  });
  return facetsIndex;
};

const resolveLinkableFacetOption = (
  facetKey: string,
  optionValue: string,
  facetType: 'single' | 'multi',
  facetsIndex: Map<string, IndexedFacetEntry>,
): ResolvedFacetOption | undefined => {
  const entry = facetsIndex.get(facetKey);
  const facet = entry?.facet;
  if (!facet || facet.type !== facetType) {
    return undefined;
  }

  const option = entry.optionsByValue.get(optionValue);
  if (!option) {
    return undefined;
  }

  if (option.count === 0 && !option.active) {
    return undefined;
  }

  return { facet, option };
};

const buildChangedFacetDetails = (
  facetKey: string,
  facet: StoreFacet,
  option: StoreFacetOption,
  optionValue: string,
): ChangedFacetDetails => ({
  key: facetKey,
  title: facet.title,
  options: [
    {
      ...option,
      active: !option.active,
      value: optionValue,
    },
  ],
});

const deriveNextFacet = (
  facets: ReduxApplicationState['productsQuery']['facets'],
  facetKey: string,
  facet: StoreFacet,
  option: StoreFacetOption,
  optionValue: string,
): StoreFacet | undefined => {
  const changedFacet = buildChangedFacetDetails(facetKey, facet, option, optionValue);
  const nextFacets = applyFacetOptionChangeToFacets(facets, changedFacet);
  return nextFacets.find((nextFacet) => nextFacet.key === facetKey);
};

const mergeFacetParamsIntoListingParams = (
  baseListingParams: URLSearchParams,
  facetKey: string,
  nextFacet?: StoreFacet,
): URLSearchParams => {
  const merged = new URLSearchParams(baseListingParams);
  merged.delete(facetKeyToListingsQueryParamName(facetKey));

  const contribution = getListingsFacetsAsURLSearchParams(nextFacet ? [nextFacet] : []);
  contribution.forEach((value, key) => {
    merged.append(key, value);
  });

  return merged;
};

const deriveFacetOptionHref = (
  state: ReduxApplicationState,
  baseListingParams: URLSearchParams,
  facetKey: string,
  optionValue: string,
  facetType: 'single' | 'multi',
  facetsIndex: Map<string, IndexedFacetEntry>,
): string | undefined => {
  const resolved = resolveLinkableFacetOption(facetKey, optionValue, facetType, facetsIndex);
  if (!resolved) {
    return undefined;
  }

  const nextFacet = deriveNextFacet(
    state.productsQuery.facets,
    facetKey,
    resolved.facet,
    resolved.option,
    optionValue,
  );
  const merged = mergeFacetParamsIntoListingParams(baseListingParams, facetKey, nextFacet);

  return buildListingsURL(state.router.location.pathname, merged, state.navigation.hash);
};

export const attachHrefToUIFacets = (
  state: ReduxApplicationState,
  uiFacets: UIFacet[],
): UIFacet[] => {
  if (uiFacets.length === 0) {
    return uiFacets;
  }

  const page = state.productsQuery.page ?? 1;
  const baseParams = adaptStoreToPageURL(state, page);
  const facetsIndex = buildFacetsIndex(state.productsQuery.facets);

  return uiFacets.map((facet) => ({
    ...facet,
    options: facet.options.map((option) => {
      const href = deriveFacetOptionHref(
        state,
        baseParams,
        facet.key,
        option.value,
        facet.type,
        facetsIndex,
      );
      return { ...option, href };
    }),
  }));
};
